import { createContext, useContext, useState, useCallback, ReactNode, useEffect } from 'react';
import { useQueryClient, useIsFetching } from '@tanstack/react-query';
import { SyncStatus } from '@/components/ui/sync-status-indicator';
import { invalidateSiteStaticCache } from '@/hooks/queries/useSiteStaticData';
import { useBackgroundSyncState } from '@/hooks/useBackgroundSync';
import { logger } from '@/lib/logger';

interface SyncContextType {
  status: SyncStatus;
  setStatus: (status: SyncStatus) => void;
  triggerGlobalRefresh: () => Promise<void>;
  isRefreshing: boolean;
}

const SyncContext = createContext<SyncContextType | undefined>(undefined);

export function SyncProvider({ children }: { children: ReactNode }) {
  const [manualStatus, setManualStatus] = useState<SyncStatus | null>(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const queryClient = useQueryClient();
  const isFetching = useIsFetching();
  const { isSyncing } = useBackgroundSyncState();

  // Compute status from various states (single source of truth for sync/refresh)
  const computedStatus: SyncStatus = (() => {
    if (manualStatus === 'refreshing' || isRefreshing) return 'refreshing';
    if (isSyncing) return 'saving';
    if (isFetching > 0) return 'refreshing'; // background refetch → "Actualisation..." in header badge
    return 'synced';
  })();

  // Reset manual status when not refreshing
  useEffect(() => {
    if (!isRefreshing && manualStatus === 'refreshing') {
      setManualStatus(null);
    }
  }, [isRefreshing, manualStatus]);

  const triggerGlobalRefresh = useCallback(async () => {
    setIsRefreshing(true);
    setManualStatus('refreshing');
    
    try {
      const siteId = localStorage.getItem('selected_site_id');
      
      if (siteId) {
        // Invalidate server-side cache
        await invalidateSiteStaticCache(siteId);
      }
      
      // Invalidate all client queries
      await queryClient.invalidateQueries();
      
      // Small delay for UI feedback
      await new Promise(resolve => setTimeout(resolve, 300));
      
      setManualStatus('synced');
    } catch (error) {
      logger.error("SyncContext", "Global refresh failed:", error);
      setManualStatus('error');
    } finally {
      setIsRefreshing(false);
    }
  }, [queryClient]);

  const setStatus = useCallback((status: SyncStatus) => {
    setManualStatus(status);
  }, []);

  return (
    <SyncContext.Provider value={{ 
      status: manualStatus ?? computedStatus, 
      setStatus, 
      triggerGlobalRefresh, 
      isRefreshing 
    }}>
      {children}
    </SyncContext.Provider>
  );
}

export function useSync() {
  const context = useContext(SyncContext);
  if (!context) {
    throw new Error('useSync must be used within SyncProvider');
  }
  return context;
}
